
import React, { useState, useMemo, useEffect } from 'react';
import {
  calculateVelocity,
  calculateReynoldsNumber,
} from '../engine/hydraulics';
import { getWaterProperties } from '../engine/fluids';
import { useLanguage } from '../i18n/LanguageContext';
import { getCalculatorToolText } from '../i18n/calculatorToolTranslations';
import { VelocityReportData } from './PrintableReport';

interface VelocityCalculatorProps {
  onPrint?: () => void;
  onReportDataChange?: (data: VelocityReportData | null) => void;
}

export const VelocityCalculator: React.FC<VelocityCalculatorProps> = ({
  onPrint,
  onReportDataChange,
}) => {
  const { language } = useLanguage();
  const t = getCalculatorToolText(language);

  const [rawFlow, setRawFlow] = useState<string>('1.2');
  const [rawId, setRawId] = useState<string>('32.6');

  const flowLps = parseFloat(rawFlow);
  const idMm = parseFloat(rawId);

  const validationError = useMemo(() => {
    if (isNaN(flowLps) || flowLps <= 0) return t.invalidFlow;
    if (isNaN(idMm) || idMm <= 0) return t.invalidID;
    return null;
  }, [flowLps, idMm, t]);

  const analysis = useMemo(() => {
    if (validationError) return null;

    const flowM3s = flowLps * 0.001;
    const v = calculateVelocity(flowM3s, idMm);
    const fluid = getWaterProperties(20);
    const Re = calculateReynoldsNumber(v, idMm, fluid.kinematicViscosity);
    const g = 9.80665;
    const velocityHeadM = Math.pow(v, 2) / (2 * g);
    const dynamicPressureKPa = (0.5 * fluid.density * Math.pow(v, 2)) / 1000;

    let verdict = t.compliant;
    let verdictColor = 'bg-emerald-100 text-emerald-800';

    if (v > 2.4) {
      verdict = t.nonCompliant;
      verdictColor = 'bg-rose-100 text-rose-800';
    } else if (v < 0.5) {
      verdict = t.lowVelocity;
      verdictColor = 'bg-amber-100 text-amber-800';
    }

    return {
      v,
      vFps: v * 3.28084,
      Re,
      velocityHeadM,
      dynamicPressureKPa,
      verdict,
      verdictColor,
    };
  }, [flowLps, idMm, validationError, t]);

  useEffect(() => {
    if (!onReportDataChange) return;

    if (validationError || !analysis) {
      onReportDataChange(null);
      return;
    }

    onReportDataChange({
      rawFlow,
      rawId,
      results: {
        velocityMs: analysis.v,
        velocityFps: analysis.vFps,
        reynoldsNumber: analysis.Re,
        velocityHeadM: analysis.velocityHeadM,
        dynamicPressureKPa: analysis.dynamicPressureKPa,
        verdict: analysis.verdict,
      },
    });
  }, [
    validationError,
    analysis,
    rawFlow,
    rawId,
    onReportDataChange,
  ]);

  return (
    <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6">
      <div className="max-w-3xl">
        <span className="text-xs font-bold uppercase tracking-wider text-[#087F9C] block mb-1">
          {t.velocityBadge}
        </span>

        <h2 className="font-display font-extrabold text-2xl text-[#10283D]">
          {t.velocityTitle}
        </h2>

        <p className="text-sm text-slate-600 mt-1">
          {t.velocityDescription}
        </p>
      </div>

      {validationError && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-xs font-semibold">
          ⚠️ {validationError}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4 border-t border-slate-100">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
            {t.flowRate}
          </label>

          <input
            type="number"
            step="0.1"
            value={rawFlow}
            onChange={(e) => setRawFlow(e.target.value)}
            className="w-full p-2.5 rounded-xl border border-slate-300 text-sm font-semibold"
          />
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
            {t.pipeID}
          </label>

          <input
            type="number"
            step="0.5"
            value={rawId}
            onChange={(e) => setRawId(e.target.value)}
            className="w-full p-2.5 rounded-xl border border-slate-300 text-sm font-semibold"
          />
        </div>
      </div>

      {analysis && (
        <div className="p-6 rounded-3xl bg-slate-50 border border-slate-200 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block">
                {t.calculatedVelocity}
              </span>

              <div className="flex items-baseline gap-2 mt-1">
                <span className="font-display font-extrabold text-3xl text-[#10283D]">
                  {analysis.v.toFixed(2)} m/s
                </span>

                <span className="text-xs font-semibold text-slate-500">
                  {analysis.vFps.toFixed(2)} ft/s
                </span>
              </div>
            </div>

            <div className={`px-3 py-1.5 rounded-xl text-xs font-extrabold ${analysis.verdictColor}`}>
              {analysis.verdict}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-3 border-t border-slate-200 text-xs">
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-bold">
                {t.velocityHead}
              </span>
              <span className="font-mono font-bold text-slate-800">
                {analysis.velocityHeadM.toFixed(3)} m of water
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-bold">
                {t.dynamicPressure}
              </span>
              <span className="font-mono font-bold text-slate-800">
                {analysis.dynamicPressureKPa.toFixed(2)} kPa
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-bold">
                {t.reynoldsNumber}
              </span>
              <span className="font-mono font-bold text-slate-800">
                {analysis.Re.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      )}

      {onPrint && analysis && (
        <div className="pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
          <span className="text-xs text-slate-500 font-medium">
            Generate verified pipe flow velocity &amp; CIBSE compliance report
          </span>
          <button
            type="button"
            onClick={onPrint}
            disabled={!!validationError}
            className="py-2.5 px-5 rounded-2xl font-bold text-xs bg-[#10283D] hover:bg-[#1E3A5F] active:bg-[#087F9C] text-white shadow-md flex items-center gap-2 transition-all duration-200 border border-slate-700/30 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg
              className="w-4 h-4 text-[#39BFD3]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
              />
            </svg>
            <span>PDF Report / Print Compliance</span>
          </button>
        </div>
      )}
    </div>
  );
};
